"use client";
import { Track } from "@/src/lib/tracking";

import { useMutation } from "@tanstack/react-query";
import { safeStringifyError } from "utils";
import { useOspApiClientProvider } from "@hooks/useOspApiClientProvider";
import type { ConfirmSaveGoalResponse } from "@features/api/ChatApi";
import type { SaveGoalArgs } from "@components/Chat/InteractiveTool/types";
import { removeSaveGoalDraftIdFromStorage } from "./saveGoalDraftIdStorage";
import { mapSaveGoalArgsToConfirmSaveRequest } from "./mapSaveGoalArgsToConfirmSaveRequest";

/**
 * save_goal 用户点击「Active my plan」后调用 POST /goals/confirm-save。
 * 请求体由 SaveGoalArgs 映射，不走 stream。
 * goalId 可选：省略时走创建 ACTIVE 分支；有值时走 INACTIVE 草稿确认。
 */
export const useConfirmSaveGoal = () => {
  const { chatApi } = useOspApiClientProvider();

  return useMutation({
    mutationFn: async (
      args: SaveGoalArgs,
    ): Promise<ConfirmSaveGoalResponse> => {
      if (!chatApi?.goals) {
        throw new Error("[useConfirmSaveGoal] chatApi.goals unavailable");
      }

      const request = mapSaveGoalArgsToConfirmSaveRequest(args);

      Track.log("info", "useConfirmSaveGoal", "confirm-save request", {
        goalId: request.goalId ?? null,
        title: request.title,
        metricsCount: request.metrics.length,
      });

      const res = await chatApi.goals.confirmSaveCreate(request);
      const data = res.data ?? {};
      // PLANNED 后端已废弃（恒为 ACTIVE）→ 草稿 id 用完即清理
      removeSaveGoalDraftIdFromStorage();
      if (res?.error) {
        Track.log("warn", "useConfirmSaveGoal", "confirm-save failed", {
          cause: String(safeStringifyError(res.error)),
        });
        throw res.error;
      }

      return data;
    },
  });
};
